import {
  applyBoardSquareRoll,
  getBoardSquareColor,
  getBoardSquareLabel,
  getBoardTileAbility,
  replaceRetiredBossTile,
} from './doorBoard';
import { boostPowerUpRoll, getPowerUpMultiplier } from './powerUp';
import { CORE_MASSIVE_ATTACK_BONUS } from './coreDamage';

export const getBoardTileInfo = (square, context = {}) => {
  if (!square) return null;
  square = replaceRetiredBossTile(square);
  const ability = getBoardTileAbility(square.effect);
  const amount = square.effect?.amount || 0;
  const modifier = square.modifier || 0;
  const notes = [];
  let name = getBoardSquareLabel(square);
  let description;
  let example;

  if (square.coreDamage) {
    switch (square.effect?.type) {
      case 'corePower':
        description = `Apply this tile's ${
          modifier > 0 ? '+' : ''
        }${modifier} damage modifier, then boost the result by +${
          square.effect.level * 20
        }% HP damage, rounded up. Interference and defenses apply afterward.`;
        break;
      case 'critical':
        description = "Double the Core's damage roll.";
        break;
      case 'criticalMiss':
        description =
          'The Core misses. Its attack deals zero damage, with no backlash.';
        break;
      case 'massiveAttack':
        description = `Charge a colossal fractured energy beam. Add ${CORE_MASSIVE_ATTACK_BONUS} to the Core's damage roll before Interference, backlash, and defenses. Meltdown's +2 is already included in the die.`;
        break;
      case 'shield':
        description = `The Core gains a shield that blocks up to ${amount} damage from your next attack.`;
        break;
      default:
        description =
          modifier === 0
            ? "The Core's damage roll stays the same."
            : `${modifier > 0 ? 'Add' : 'Subtract'} ${Math.abs(modifier)} ${
                modifier > 0 ? 'to' : 'from'
              } the Core's damage roll, to a minimum of zero.`;
    }
    const changesDamage =
      !square.effect ||
      ['critical', 'criticalMiss', 'massiveAttack', 'corePower'].includes(
        square.effect.type
      );
    return {
      name,
      description,
      notes,
      color: getBoardSquareColor(square),
      example: changesDamage
        ? `A Core roll of 3 becomes ${applyBoardSquareRoll(
            3,
            square
          )} damage before defenses.`
        : undefined,
    };
  }

  if (ability) {
    name = ability.name;
    description = ability.description;
    if (ability.coreOnly && !context.core) {
      notes.push('This effect is only active in the Core fight.');
    }
    if (square.effect.type === 'powerUp') {
      example = `A roll of 50 becomes ${boostPowerUpRoll(
        50,
        getPowerUpMultiplier(square.effect)
      )} before the die lands again.`;
    } else if (
      square.effect.type === 'gambit' &&
      Number.isFinite(context.shards)
    ) {
      notes.push(
        context.shards > 0
          ? `You currently have ${context.shards} ${
              context.shards === 1 ? 'shard' : 'shards'
            }; spend 1 to reroll and relaunch the die.`
          : 'You have no shards, so you can only keep this roll.'
      );
    }
  } else {
    switch (square.effect?.type) {
      case 'corruption':
        name = 'Corrupted · −100';
        description =
          'The Core replaced a beneficial tile with corruption. Subtract 100 from your roll. Aim around it; corruption lasts for the rest of this fight.';
        break;
      case 'critical':
        name = 'Critical Hit · ×2';
        description = 'Double the die’s number.';
        break;
      case 'criticalMiss':
        name = 'Critical Miss · ÷3';
        description =
          'Divide the die’s number by 3 and round to the nearest whole number.';
        break;
      case 'heal':
        name = `Healing · +${amount} HP`;
        description = `Restore ${amount} HP. Any healing above your current maximum permanently increases your max HP for this run.`;
        if (amount === 2)
          description +=
            ' After activation, this tile becomes a -3 HP hazard until you enter a new sector.';
        break;
      case 'shield':
        name = `Block ${amount} damage`;
        description = context.core
          ? `Protect your die with a shield that blocks up to ${amount} damage from the Core’s upcoming counterattack.`
          : `Protect your die with a shield that blocks up to ${amount} damage if you fail this room.`;
        break;
      case 'relic':
        name = 'Treasure Chest';
        description = 'Open a chest and reveal a random relic after landing.';
        break;
      case 'shards':
        name = `+${amount} ${amount === 1 ? 'Shard' : 'Shards'}`;
        description = `Collect ${amount} ${
          amount === 1 ? 'shard' : 'shards'
        } when this room resolves. Shards are currency for purchases, repairs, and rerolls.`;
        break;
      case 'damage':
        name = `${amount} HP damage`;
        description = `Lose ${amount} HP when this room resolves.`;
        break;
      case 'massiveAttack':
        name = 'MASSIVE ATTACK';
        description = `Charge a colossal fractured energy beam. Add ${CORE_MASSIVE_ATTACK_BONUS} HP damage to the Core's die, then apply Interference, backlash, and your defenses. There is no second damage roll.`;
        break;
      default:
        description = 'The die’s number stays the same.';
        if (square.operation === 'multiply') {
          description = `Multiply the die’s number by ${square.value}.`;
        } else if (square.operation === 'divide') {
          description = `Divide the die’s number by ${square.value} and round down.`;
        } else if (modifier > 0) {
          description = `Add ${modifier} to the die’s number.`;
        } else if (modifier < 0) {
          description = `Subtract ${Math.abs(modifier)} from the die’s number.`;
        }
        break;
    }
    const changesRoll =
      ['critical', 'criticalMiss'].includes(square.effect?.type) ||
      square.operation ||
      modifier;
    if (changesRoll) {
      const result = applyBoardSquareRoll(50, square);
      if (Number.isFinite(result)) example = `A roll of 50 becomes ${result}.`;
      if (
        modifier &&
        square.effect?.type !== 'critical' &&
        (square.effect || square.operation)
      ) {
        notes.push(
          `${modifier > 0 ? 'Add' : 'Subtract'} ${Math.abs(modifier)} ${
            square.operation || square.effect?.type === 'criticalMiss'
              ? 'afterward'
              : 'to the die’s number'
          }.`
        );
      }
    }
  }

  return {
    name,
    description,
    example,
    notes,
    color: getBoardSquareColor(square),
  };
};
